import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  apiClient,
  onSessionRenewed,
  performSilentRefresh,
} from "../api/client";
import { tokenStore } from "../api/tokenStore";
import { sessionChange, sessionChangeNotice } from "./sessionChange";

const AuthContext = createContext({
  user: null,
  localUser: null,
  loading: true,
  login: async () => {},
  adoptSession: async () => {},
  logout: async () => {},
  refreshUser: async () => {},
  signOut: async () => {},
  sessionNotice: null,
  dismissSessionNotice: () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Why this tab's account changed under the person (components/SessionNotice.jsx).
  const [sessionNotice, setSessionNotice] = useState(null);

  const refreshUser = useCallback(async () => {
    try {
      // /api/me returns full profile with avatarDownloadUrl and related relations
      const res = await apiClient.get("/me");
      const me = res.data;
      setUser(me);
      return me;
    } catch {
      try {
        const res = await apiClient.get("/auth/me");
        const me = res.data;
        setUser(me);
        return me;
      } catch (err) {
        setUser(null);
        return null;
      }
    }
  }, []);

  useEffect(() => {
    let isMounted = true;

    async function initAuth() {
      try {
        // Attempt silent refresh using HttpOnly cookie to populate in-memory access token
        const token = await performSilentRefresh();
        if (token) {
          try {
            const res = await apiClient.get("/me");
            if (isMounted) {
              setUser(res.data);
            }
          } catch {
            const res = await apiClient.get("/auth/me");
            if (isMounted) {
              setUser(res.data);
            }
          }
        } else {
          if (isMounted) {
            setUser(null);
          }
        }
      } catch (err) {
        if (isMounted) {
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    initAuth();

    return () => {
      isMounted = false;
    };
  }, []);

  // The browser's session is shared by its tabs. When a renewal shows that
  // it now belongs to another account (signed in from another tab) or has
  // ended (signed out there), this tab follows it and says so, instead of
  // keeping the old account's pages with the new account's token.
  useEffect(
    () =>
      onSessionRenewed(async (renewed) => {
        const change = sessionChange(user, renewed);
        if (!change) return;
        if (change === "ended") {
          tokenStore.clearAccessToken();
          setUser(null);
        } else {
          await refreshUser();
        }
        setSessionNotice(sessionChangeNotice(change, renewed));
      }),
    [user, refreshUser],
  );

  const dismissSessionNotice = useCallback(() => setSessionNotice(null), []);

  // Keeps a session the server has just issued, from signing in or from
  // reactivating a deleted account, and loads the full profile.
  const adoptSession = useCallback(async ({ accessToken, user: userData }) => {
    if (accessToken) {
      tokenStore.setAccessToken(accessToken);
    }
    try {
      const meRes = await apiClient.get("/me");
      const fullUser = meRes.data;
      setUser(fullUser);
      return fullUser;
    } catch {
      setUser(userData);
      return userData;
    }
  }, []);

  const login = useCallback(
    async (email, password) => {
      const res = await apiClient.post("/auth/login", { email, password });
      return adoptSession(res.data);
    },
    [adoptSession],
  );

  const logout = useCallback(async () => {
    try {
      await apiClient.post("/auth/logout");
    } catch {
      // Ignore errors on logout
    } finally {
      tokenStore.clearAccessToken();
      try {
        localStorage.removeItem("hr_job_applicants");
        localStorage.removeItem("published_opportunities");
        Object.keys(localStorage).forEach((key) => {
          if (
            key.startsWith("hr_job_applicants_") ||
            key.startsWith("published_opportunities_")
          ) {
            localStorage.removeItem(key);
          }
        });
      } catch {
        // Ignore localStorage cleanup errors
      }
      setUser(null);
    }
  }, []);

  const value = useMemo(
    () => ({
      user,
      localUser: user, // Backward compatibility for components checking localUser
      loading,
      login,
      adoptSession,
      logout,
      signOut: logout,
      refreshUser,
      refreshLocalUser: refreshUser,
      sessionNotice,
      dismissSessionNotice,
    }),
    [
      user,
      loading,
      login,
      adoptSession,
      logout,
      refreshUser,
      sessionNotice,
      dismissSessionNotice,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}
